import React, { useEffect, useRef, useState } from 'react'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import CompetitorSelect from '../../CompetitorSelect'
import CompetitionSelect, { CompetitionFilter } from '../../CompetitionSelect'
import SeasonSelect from '../../SeasonSelect'
import { api } from '../../../services/api'
import { fetchVodTypes } from '../../../services/vodApi'
import { Opt } from '../../../constants/carouselOptions'
import selectStyles from '../../../styles/react-select.module.scss'
import { Competitor, Season } from '../../../types/forms'

// All carousel lookup filters speak ID arrays (the wire contract in
// metadata.filters). The reused typeaheads work in rich objects, so each
// adapter keeps a session label cache: items the user picks are cached, and IDs
// loaded from a saved config are batch-resolved to their real labels on mount
// (via the list endpoints' `ids` param) so editing an existing widget shows
// friendly names instead of raw UUIDs.
export interface LookupProps {
  ids: string[]
  onChange: (ids: string[]) => void
  placeholder?: string
}

// Every catalog typeahead we wrap keys its options by tc_id and is multi-select.
// makeLookupAdapter bridges one to the ID-array LookupProps contract: it maps
// the stored IDs to cached rich objects (falling back to a raw-ID stub until
// resolved), caches whatever the user picks, and batch-resolves any uncached
// IDs on mount so saved filters rehydrate their labels.
const makeLookupAdapter = <T extends { tc_id: string }>(
  Component: React.ComponentType<any>,
  fallback: (id: string) => T,
  resolve: (ids: string[]) => Promise<T[]>,
  defaultPlaceholder: string,
  extraProps: Record<string, unknown> = {},
): React.FC<LookupProps> => {
  const Adapter: React.FC<LookupProps> = ({ ids, onChange, placeholder }) => {
    const cache = useRef<Map<string, T>>(new Map())
    const [, forceRender] = useState(0)

    // Rehydrate labels for any stored IDs the user hasn't picked this session.
    useEffect(() => {
      const missing = ids.filter(id => !cache.current.has(id))
      if (missing.length === 0) return
      let cancelled = false
      resolve(missing)
        .then(items => {
          if (cancelled) return
          items.forEach(o => cache.current.set(o.tc_id, o))
          forceRender(n => n + 1)
        })
        .catch(() => {})
      return () => {
        cancelled = true
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ids.join(',')])

    const value: T[] = ids.map(id => cache.current.get(id) ?? fallback(id))
    return (
      <Component
        isMulti
        placeholder={placeholder ?? defaultPlaceholder}
        value={value}
        onChange={(v: T[] | null) => {
          const arr = v ?? []
          arr.forEach(o => cache.current.set(o.tc_id, o))
          onChange(arr.map(o => o.tc_id))
        }}
        {...extraProps}
      />
    )
  }
  return Adapter
}

export const PlayerFilterSelect = makeLookupAdapter<Competitor>(
  CompetitorSelect,
  (id) => ({ tc_id: id, ext_id: '', name: id }),
  async (ids) => {
    const resp = await api.get('/competitors', { ids: ids.join(','), limit: 100 })
    return (resp?.competitors || []).map((c: any) => ({
      tc_id: c.tc_competitor_id,
      ext_id: c.ext_competitor_id ?? '',
      name: c.tc_name || c.ext_name || c.tc_competitor_id,
      team_type: c.tc_team_type || c.ext_team_type || 'singles',
    }))
  },
  'Search players…',
  { teamType: 'mixed' },
)

export const CompetitionFilterSelect = makeLookupAdapter<CompetitionFilter>(
  CompetitionSelect,
  (id) => ({ tc_id: id, name: id }),
  async (ids) => {
    const resp = await api.get('/competitions', { ids: ids.join(','), limit: 100 })
    return (resp?.competitions || []).map((c: any) => ({
      tc_id: c.tc_competition_parent_id,
      name: c.tc_name || c.ext_name || c.tc_competition_parent_id,
    }))
  },
  'Search tournaments…',
)

export const SeasonFilterSelect = makeLookupAdapter<Season>(
  SeasonSelect,
  (id) => ({ tc_id: id, ext_id: '', name: id, year: 0 }),
  async (ids) => {
    const resp = await api.get('/seasons', { ids: ids.join(','), limit: 100 })
    return (resp?.seasons || []).map((s: any) => ({
      tc_id: s.tc_season_id,
      ext_id: s.ext_season_id ?? '',
      name: s.tc_name || s.ext_name || s.tc_season_id,
      year: s.tc_year || s.ext_year || 0,
    }))
  },
  'Search seasons…',
)

// VOD content types are a small closed set, so this fetches the whole catalog
// once instead of wrapping a search typeahead like the adapters above. It is
// still catalog-backed rather than a hardcoded enum: tc_vod_type owns which
// types are active and what they are called, and GET /vod/types serves exactly
// that (its docstring names CMS filters as the consumer).
//
// The stored value is the vod_type_code, which is also what ACE matches on, so
// there is no id/label indirection to cache — an unresolved code can be shown
// as-is rather than as a stub.
export const VodTypeFilterSelect: React.FC<LookupProps> = ({ ids, onChange, placeholder }) => {
  const [options, setOptions] = useState<Opt[]>([])
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetchVodTypes()
      .then((resp) => {
        if (cancelled) return
        setOptions(
          (resp?.types ?? []).map((t) => ({ value: t.vod_type_code, label: t.display_name })),
        )
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Saved codes stay visible while the catalog loads, or if it fails to load —
  // dropping them from the control would silently rewrite the filter on save.
  const value: Opt[] = ids.map(
    (id) => options.find((o) => o.value === id) ?? { value: id, label: id },
  )

  return (
    <Select<Opt, true>
      isMulti
      options={options}
      value={value}
      isLoading={!failed && options.length === 0}
      onChange={(v) => onChange((v ?? []).map((o) => o.value))}
      placeholder={
        failed ? 'Could not load content types' : (placeholder ?? 'Select content types…')
      }
      className={selectStyles.reactSelect}
      classNamePrefix="react-select"
    />
  )
}

// Merchandising tag keys, from the same catalog the VOD editor writes
// (GET /publishing/tag-options). Creatable, because a rail is often built for a tag the
// content team is about to start applying — the filter has to be able to name
// one that has no assets yet. The stored value is the tag key itself, which is
// what ACE matches on, so an unresolved key shows as-is.
export const TagFilterSelect: React.FC<LookupProps> = ({ ids, onChange, placeholder }) => {
  const [options, setOptions] = useState<Opt[]>([])
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    api
      .get<{ tags: Array<{ value: string; label: string }> }>('/publishing/tag-options')
      .then((resp) => {
        if (cancelled) return
        setOptions((resp?.tags ?? []).map((t) => ({ value: t.value, label: t.label || t.value })))
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const value: Opt[] = ids.map(
    (id) => options.find((o) => o.value === id) ?? { value: id, label: id },
  )

  return (
    <CreatableSelect<Opt, true>
      isMulti
      options={options}
      value={value}
      isLoading={!failed && options.length === 0}
      onChange={(v) => onChange((v ?? []).map((o) => o.value))}
      formatCreateLabel={(input) => `Use tag “${input}”`}
      placeholder={failed ? 'Could not load tags' : (placeholder ?? 'Select or type tag keys…')}
      className={selectStyles.reactSelect}
      classNamePrefix="react-select"
    />
  )
}
